import { useState } from "react";

// components
import {
  Header,
  Body,
  Footer,
  Button,
  Search
} from "../../../../components/UI";
import { Heading2, Paragraph } from "../../../../components/typography";
// builders
import * as buttonDesignType from "../../../../builders/buttonDesign.types";
// constants
import * as modalTypes from "../../../../constants/generalSettingsModal.constants";
// SVGS
import { Close } from "../../../../SVGS";
// styles
import { GeneralSettingsListModalStyles } from "./generalSettingsListModal.styles";

const modalBuilder = {
  [modalTypes.SUPPORTED_TRUCKS]: {
    title: "Supported Trucks",
    inputLabel: "Add New Supported Truck",
    inputPlaceholder: "Select a truck",
    listLabel: "Supported Trucks"
  },
  [modalTypes.SUPPORTED_TRAILERS]: {
    title: "Supported Trailers",
    inputLabel: "Add New Supported Trailer",
    inputPlaceholder: "Select a trailer",
    listLabel: "Supported Trailers"
  },
  [modalTypes.SUPPORTED_TRAILER_TYPES]: {
    title: "Supported Trailer Types",
    inputLabel: "Add New Supported Trailer Type",
    inputPlaceholder: "Select a trailer type",
    listLabel: "Supported Trailer Types"
  },
  [modalTypes.SUPPORTED_PAYMENT_TYPES]: {
    title: "Payment Options",
    inputLabel: "Add New Payment Option",
    inputPlaceholder: "Select a payment option",
    listLabel: "Payment Options"
  }
};

const GeneralSettingsListModal = ({
  modalType,
  trucks,
  trailers,
  trailerTypes,
  paymentTypes,
  truckOptions,
  trailerOptions,
  trailerTypeOptions,
  paymentOptions,
  saveModalChanges,
  closeModal
}) => {
  const setValuesBasedOnModalType = () => {
    if (modalType === modalTypes.SUPPORTED_TRUCKS)
      return { value: trucks, options: truckOptions };
    if (modalType === modalTypes.SUPPORTED_TRAILERS)
      return { value: trailers, options: trailerOptions };
    if (modalType === modalTypes.SUPPORTED_TRAILER_TYPES)
      return { value: trailerTypes, options: trailerTypeOptions };
    if (modalType === modalTypes.SUPPORTED_PAYMENT_TYPES)
      return { value: paymentTypes, options: paymentOptions };
    return { value: [], options: [] };
  };

  const labelKey =
    modalType === modalTypes.SUPPORTED_TRUCKS ||
    modalType === modalTypes.SUPPORTED_TRAILERS
      ? "manufacturer"
      : "name";

  const valueKey =
    modalType === modalTypes.SUPPORTED_PAYMENT_TYPES ? "key" : "id";

  const [modalValue, setModalValue] = useState(setValuesBasedOnModalType());
  const [availableFilter, setAvailableFilter] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");

  const { title } = modalBuilder[modalType];

  const getItemId = item => item[valueKey];

  const selectedIds = modalValue.value.map(getItemId);

  const availableItems = modalValue.options.filter(
    option => !selectedIds.includes(getItemId(option))
  );

  const selectedItems = modalValue.options.filter(option =>
    selectedIds.includes(getItemId(option))
  );

  const filteredAvailable = availableFilter
    ? availableItems.filter(item =>
        item[labelKey]?.toLowerCase().includes(availableFilter.toLowerCase())
      )
    : availableItems;

  const filteredSelected = selectedFilter
    ? selectedItems.filter(item =>
        item[labelKey]?.toLowerCase().includes(selectedFilter.toLowerCase())
      )
    : selectedItems;

  const addItem = item => {
    setModalValue(prev => ({
      ...prev,
      value: [...prev.value, item]
    }));
  };

  const removeItem = id => {
    setModalValue(prev => ({
      ...prev,
      value: prev.value.filter(item => getItemId(item) !== id)
    }));
  };

  return (
    <GeneralSettingsListModalStyles>
      <Header className="header">
        <Heading2 className="title">{title}</Heading2>
        <Close onClick={closeModal} />
      </Header>
      <Body>
        <div className="transferContainer">
          {/* Available column */}
          <div className="transferColumn">
            <div className="columnHeader">
              <span className="columnLabel">Available</span>
              <span className="columnCount">{availableItems.length}</span>
            </div>
            <div className="searchWrapper">
              <Search
                placeholder="Search..."
                value={availableFilter}
                onChange={e => setAvailableFilter(e.target.value)}
              />
            </div>
            <div className="columnList">
              {filteredAvailable.length === 0 ? (
                <div className="emptyState">
                  <Paragraph>
                    {availableItems.length === 0
                      ? "All items selected"
                      : "No matches found"}
                  </Paragraph>
                </div>
              ) : (
                filteredAvailable.map(item => (
                  <div
                    key={getItemId(item)}
                    className="transferItem"
                    onClick={() => addItem(item)}
                  >
                    <span className="itemName">{item[labelKey]}</span>
                    <span className="actionIcon addIcon">+</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Selected column */}
          <div className="transferColumn">
            <div className="columnHeader">
              <span className="columnLabel">Selected</span>
              <span className="columnCount">{selectedItems.length}</span>
            </div>
            <div className="searchWrapper">
              <Search
                placeholder="Search..."
                value={selectedFilter}
                onChange={e => setSelectedFilter(e.target.value)}
              />
            </div>
            <div className="columnList">
              {filteredSelected.length === 0 ? (
                <div className="emptyState">
                  <Paragraph>
                    {selectedItems.length === 0
                      ? "No items selected"
                      : "No matches found"}
                  </Paragraph>
                </div>
              ) : (
                filteredSelected.map(item => (
                  <div
                    key={getItemId(item)}
                    className="transferItem"
                    onClick={() => removeItem(getItemId(item))}
                  >
                    <span className="itemName">{item[labelKey]}</span>
                    <span className="actionIcon removeIcon">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Body>
      <Footer className="footer">
        <Button
          className="submitButton"
          type="button"
          onClick={() => saveModalChanges(modalValue.value)}
          buttonDesignType={buttonDesignType.PRIMARY}
        >
          Save Changes
        </Button>
      </Footer>
    </GeneralSettingsListModalStyles>
  );
};

export default GeneralSettingsListModal;
